/**
 * Shared utilities for dashboard/calendar task visibility.
 * Mirrors reminderUtils.ts — see that file for the declutter-filter rationale.
 */

import type { Pipeline } from '@/lib/crm/hooks/usePipelines';
import { TASK_STAGE_ASSOCIATION_TYPE_NAME } from '@/lib/crm/record/task/task.constants';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

export { TASK_STAGE_ASSOCIATION_TYPE_NAME };

/** Canonical fallback title shown when a task has neither a display name nor a title. */
const UNTITLED_TASK_TITLE = 'Untitled task';

/**
 * Resolve a task's display title: `ahoy_display_name`, falling back to
 * `ahoy_title`, falling back to a canonical placeholder. Shared by TaskRow
 * (dashboard) and mapTaskToCalendarEvent (calendar) so both surfaces render
 * the identical fallback chain instead of two independently worded ones.
 */
export function resolveTaskTitle(task: CrmObject): string {
  return (
    getPropertyValue<string>(task.properties?.ahoy_display_name) ||
    getPropertyValue<string>(task.properties?.ahoy_title) ||
    UNTITLED_TASK_TITLE
  );
}

/**
 * Association type name for ahoy_task's owner. Defined here as a named
 * constant so it has a single source of truth across the codebase.
 * `TASK_STAGE_ASSOCIATION_TYPE_NAME` above is re-exported from
 * `task.constants.ts`, the single source of truth for that constant.
 *
 * Coordinated with the sibling "Task associations, pipeline expand,
 * permissions" issue, which delivers these two association types
 * (object->stage and owner) on the backend — a rename there is a
 * single-line fix here.
 */
export const TASK_OWNER_ASSOCIATION_TYPE_NAME = 'ahoy_task_to_owner';

/**
 * The owner of a task, resolved from the `ahoy_task_to_owner` association.
 * Returns the owner's user entity id, or null when the task is unowned.
 */
export function taskOwnerEntityId(task: CrmObject): string | null {
  return (
    task.associations?.find(
      a => a.association_type_name === TASK_OWNER_ASSOCIATION_TYPE_NAME
    )?.target_entity_id ?? null
  );
}

/**
 * Whether a task should be shown to the current user on the dashboard /
 * calendar. A task is shown when it is unowned (visible to everyone) or its
 * owner is this user; tasks owned by someone else are hidden. This is a
 * declutter filter over data the CRM already authorized the user to read —
 * not a confidentiality boundary. Mirrors isReminderVisibleToUser.
 *
 * `currentUserEntityId` may be null (the user's entity id has not resolved
 * yet); in that case ownership cannot be proven, so only unowned tasks are
 * shown.
 */
export function isTaskVisibleToUser(
  task: CrmObject,
  currentUserEntityId: string | null
): boolean {
  const ownerId = taskOwnerEntityId(task);
  return ownerId === null || ownerId === currentUserEntityId;
}

/**
 * Whether a task's current stage (read fresh from the store via the
 * `pipelines` passed in) is terminal. A task with no stage edge, or a stage
 * that isn't found in any pipeline, is NOT terminal — this preserves the
 * "pipeline with no terminal stage" and "still-loading pipelines" cases,
 * so a task is never hidden on ambiguous data.
 */
export function isTaskInTerminalStage(
  task: CrmObject,
  pipelines: Pipeline[]
): boolean {
  const stageId =
    task.associations?.find(
      a => a.association_type_name === TASK_STAGE_ASSOCIATION_TYPE_NAME
    )?.target_entity_id ?? null;
  if (!stageId) {
    return false;
  }
  for (const p of pipelines) {
    const stage = p.stages.find(s => s.entity_id === stageId);
    if (stage) {
      return stage.is_terminal === true;
    }
  }
  return false;
}
